/**
 * AccessHub catalog — dump ∩ filter ∩ tools.
 * Live apps read this. AccessHub writes it.
 */

import type { Connection } from "./connections.js";

export const DUMP_ORIGIN = "https://proxy.vividlex.com";

/** Primary generate tool per app — AccessHub Pick on this row drives the app. */
export const PRIMARY_TOOL: Record<string, string> = {
  vividlex: "imagine-stills",
  atherra: "mimic",
  bookforge: "rewrite",
  textforge: "write",
  imageforge: "generate",
  videoforge: "generate",
  voiceforge: "tts",
  musicforge: "make-song",
  chatforge: "chat",
  visionforge: "ask-vlm",
  agentforge: "brain",
  learningforge: "library",
  coldforge: "discover",
  tradeforge: "mimic",
};

export type CatalogDump = {
  id: string;
  modelId?: string;
  name?: string;
  modality?: string;
  provider?: string;
  source_tab?: string;
};

export type CatalogRuntime = {
  ok: boolean;
  enabled: boolean;
  app: string;
  tool_id: string;
  label?: string;
  kind?: string;
  coin_cents?: number;
  model_selector?: boolean;
  dump_id?: string | null;
  dump?: CatalogDump | null;
  reason?: string;
};

export type CatalogBoardTool = {
  tool_id: string;
  label: string;
  kind: string;
  model_type?: string | null;
  dump_id?: string | null;
  dump_name?: string | null;
  family_id?: string | null;
  family_label?: string | null;
};

export type CatalogBoard = {
  ok: boolean;
  app: string;
  model_selector?: boolean;
  tools: CatalogBoardTool[];
  hidden: string[];
};

/** User overlay on AccessHub orchestration — this spin-up, per tool. */
export type ToolBackend = {
  dumpId: string;
  modelId: string;
  name: string;
  toolId: string;
};

function backendsKey(app: string): string {
  return `fgp.toolBackends.v1.${app}`;
}

export function loadToolBackends(app: string): Record<string, ToolBackend> {
  if (typeof localStorage === "undefined" || !app) return {};
  try {
    const raw = localStorage.getItem(backendsKey(app));
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== "object") return {};
    const out: Record<string, ToolBackend> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, ToolBackend>)) {
      if (v && typeof v === "object" && v.dumpId) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

export function saveToolBackend(app: string, backend: ToolBackend): Record<string, ToolBackend> {
  const all = loadToolBackends(app);
  if (!app || !backend.toolId) return all;
  all[backend.toolId] = backend;
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(backendsKey(app), JSON.stringify(all));
    } catch {
      /* quota */
    }
  }
  return all;
}

export function toolNeedsBackend(tool: { kind?: string } | null | undefined): boolean {
  return String(tool?.kind || "model") !== "local";
}

function origin(base?: string): string {
  return (base || DUMP_ORIGIN).replace(/\/$/, "");
}

/** Query for dump ∩ app filter ∩ this tool's model type. */
export function dumpModelsQuery(opts: {
  app: string;
  tab: string;
  tool?: string;
  q?: string;
}): URLSearchParams {
  const qs = new URLSearchParams({ app: opts.app, tab: opts.tab });
  const tool = (opts.tool || "").trim();
  if (tool) qs.set("tool", tool);
  if (opts.q) qs.set("q", opts.q);
  return qs;
}

export async function fetchCatalogTools(
  app: string,
  opts?: { viewport?: string; base?: string },
): Promise<CatalogBoard> {
  const qs = new URLSearchParams({ app });
  if (opts?.viewport) qs.set("viewport", opts.viewport);
  const res = await fetch(`${origin(opts?.base)}/api/catalog/tools?${qs}`);
  if (!res.ok) throw new Error(`catalog tools ${res.status}`);
  return (await res.json()) as CatalogBoard;
}

export async function fetchCatalogRuntime(
  app: string,
  tool: string,
  opts?: { base?: string },
): Promise<CatalogRuntime> {
  const qs = new URLSearchParams({ app, tool });
  const res = await fetch(`${origin(opts?.base)}/api/catalog/runtime?${qs}`);
  if (!res.ok) throw new Error(`catalog runtime ${res.status}`);
  return (await res.json()) as CatalogRuntime;
}

export function toolIsOn(board: CatalogBoard | null | undefined, toolId: string): boolean {
  if (!board) return true;
  if ((board.hidden || []).includes(toolId)) return false;
  return (board.tools || []).some((t) => t.tool_id === toolId);
}

/** Headers every generate call must send so AccessHub Pick is the model. */
export function catalogHeaders(app: string, tool: string): Record<string, string> {
  const out: Record<string, string> = { "X-Client-Product": app };
  if (tool) out["X-Suite-Tool"] = tool;
  return out;
}

export function dumpModelId(rt: CatalogRuntime | null | undefined): string | null {
  const id = rt?.dump?.modelId || "";
  return id.trim() || null;
}

/** AccessHub dump row → generate model id (hf: / cf: / civitai:). */
export function taggedDumpModel(
  dump: CatalogDump | null | undefined,
  dumpId?: string | null,
): string | null {
  const id = String(dump?.id || dumpId || "").trim();
  const mid = String(dump?.modelId || "").trim();
  const tab = String(dump?.source_tab || dump?.provider || "").toLowerCase();
  const prefix = (): "hf" | "cf" | "civitai" => {
    if (/^civitai:/i.test(id) || tab.includes("civitai")) return "civitai";
    if (
      /^(cloudflare|cf):/i.test(id) ||
      tab.includes("cloudflare") ||
      tab === "cf" ||
      tab === "pixverse"
    )
      return "cf";
    return "hf";
  };
  const bare = (s: string) => s.replace(/^(hf|cf|civitai|cloudflare):/i, "").trim();
  const p = prefix();
  if (mid) return `${p}:${bare(mid)}`;
  if (!id) return null;
  let rest = bare(id);
  if (p === "hf") rest = rest.split(":")[0] || rest;
  return rest ? `${p}:${rest}` : null;
}

export function pickEngineFromRuntime(rt: CatalogRuntime | null | undefined): {
  id: string;
  name: string;
} | null {
  if (!rt?.enabled) return null;
  const id = taggedDumpModel(rt.dump, rt.dump_id);
  if (!id) return null;
  return { id, name: String(rt.dump?.name || id.split("/").pop() || id) };
}

/** GpuBrowser is dump ∩ filter. Matches origin Boolean(snap.model_selector). */
export function gpuBrowserOn(
  rt: { model_selector?: boolean } | null | undefined,
): boolean {
  return Boolean(rt?.model_selector);
}

const GPU_BROWSER_TABS = new Set([
  "production",
  "hf",
  "cloudflare",
  "civitai",
  "forge_endpoint",
]);

export function isGpuBrowserTab(mode: string): boolean {
  return GPU_BROWSER_TABS.has(mode);
}

/** Catalog tabs exist only for the in-app picker. */
export function chromeModesForSelector<T extends string>(
  modes: readonly T[],
  selectorOn: boolean,
): T[] {
  if (selectorOn) return [...modes];
  return modes.filter((m) => !isGpuBrowserTab(m));
}

/** Dump row from GET /api/catalog/models?tab=forge_endpoint. */
export type DumpEndpointRow = {
  id?: string;
  skuId?: string;
  modelId?: string;
  name?: string;
  modality?: string;
  url?: string | null;
  status?: string | null;
  purpose?: string | null;
};

/**
 * GpuBrowser Forge endpoints tab: dump ∩ app filter, joined to origin
 * connections for Use / Wake / Sleep. Empty dump falls back to origin.
 */
export function dumpRowsToForgeConnections(
  dump: DumpEndpointRow[],
  origin: Connection[] = [],
): Connection[] {
  const boxes = origin.filter((c) => c.kind === "forge_endpoint");
  if (!dump.length) return boxes;
  const byName = new Map<string, Connection>();
  for (const c of boxes) {
    const key = String(c.sourceId || c.modelId || "").trim().toLowerCase();
    if (key) byName.set(key, c);
  }
  const now = "1970-01-01T00:00:00.000Z";
  return dump.map((row) => {
    const name = String(row.modelId || "").trim();
    const hit = byName.get(name.toLowerCase());
    const dumpId = String(row.id || row.skuId || "").trim() || `forge_endpoint:${name}`;
    return {
      id: hit?.id || dumpId,
      kind: "forge_endpoint",
      sourceTable: hit?.sourceTable || "catalog",
      sourceId: hit?.sourceId || name,
      appOrigin: hit?.appOrigin ?? null,
      label: row.name || hit?.label || name,
      modelId: name || hit?.modelId,
      engine: hit?.engine || "hf-endpoint",
      modality: row.modality || hit?.modality || "text-generation",
      endpointUrl: row.url || hit?.endpointUrl || null,
      status: hit?.status || String(row.status || "sleeping"),
      ready: hit?.ready ?? false,
      visibility: hit?.visibility || "public",
      lastHealthAt: hit?.lastHealthAt ?? null,
      lastUsedAt: hit?.lastUsedAt ?? null,
      createdAt: hit?.createdAt || now,
      updatedAt: hit?.updatedAt || now,
      skuId: row.skuId || row.id || null,
      dumpId,
      detail: hit?.detail ?? null,
    };
  });
}

export async function fetchPrimaryRuntime(
  app: string,
  opts?: { tool?: string; base?: string },
): Promise<CatalogRuntime> {
  const tool = opts?.tool || PRIMARY_TOOL[app];
  if (!tool) {
    return { ok: false, enabled: false, app, tool_id: "", reason: "unknown_app" };
  }
  return fetchCatalogRuntime(app, tool, { base: opts?.base });
}
